/**
 * /v1/records/* — 歷史紀錄
 *
 * 路徑集中在檔頭的 maps；baseURL 走 http.ts 預設（VITE_AUTH_API_URL）。
 *
 * 參考 shared 專案 `app/composables/useHistoryRecords.ts`：
 *   - 後端拿 RFC 3339 / ISO 8601 帶時區 offset 的時間字串
 *   - 預設分頁 page=1, size=20
 *
 * 呼叫範例：
 *   const { l, tt } = await fetchRecords({
 *     gameCode: 'wldd',
 *     page: 1,
 *     size: 20,
 *     start_time: historyStore.start_time,  // Unix 秒
 *     end_time: historyStore.end_time,
 *   })
 */
import dayjs from 'dayjs'
import { useHttp } from '../http'
import type { FetchRecordsParams, FetchRecordsResponse, FetchRecordsResult } from './types'

const maps = {
  /** GET /v1/records/games/{gameCode}/results */
  results: (gameCode: string): string =>
    `/v1/records/games/${encodeURIComponent(gameCode)}/results`,
} as const

/**
 * Unix 秒 → RFC 3339 / ISO 8601 帶「目標顯示時區」的 offset。
 *
 * 例：台北 (offsetMs = 28_800_000、isoOffset = '+08:00')
 *      sec 2026-05-14 16:00 UTC → 顯示 = 2026-05-15 00:00 → '2026-05-15T00:00:00+08:00'
 *
 * 注意：offset 一律由呼叫端（historyStore）帶入，**這裡不自己讀**
 * `d.getTimezoneOffset()` —— query 與顯示必須共用同一個 offset 來源，
 * 兩邊各讀各的曾讓後端比對範圍跟用戶看到的日期錯開最多 1 天。
 * （store 的 offset 現在就是用戶瀏覽器時區，2026-09 起；見 history.ts。）
 */
const toRfc3339 = (sec: number, offsetMs: number, isoOffset: string): string => {
  // 偽 UTC：用 dayjs.utc 在 UTC 視角讀 instant+offset 等於目標時區本地時刻；
  // format 直接拿 'YYYY-MM-DDTHH:mm:ss' 再接 ISO offset 字串。
  return dayjs.utc(sec * 1000 + offsetMs).format('YYYY-MM-DDTHH:mm:ss') + isoOffset
}

/**
 * 取得歷史紀錄列表
 * GET /v1/records/games/{gameCode}/results
 *
 * @throws ApiError 401（token 失效）/ 業務錯
 */
export const fetchRecords = (params: FetchRecordsParams): Promise<FetchRecordsResult> => {
  const {
    gameCode,
    page = 1,
    size = 20,
    start_time,
    end_time,
    timezoneOffsetMs,
    timezoneIso,
  } = params

  return useHttp.get<FetchRecordsResponse>(maps.results(gameCode), {
    page,
    size,
    start_time: toRfc3339(start_time, timezoneOffsetMs, timezoneIso),
    end_time: toRfc3339(end_time, timezoneOffsetMs, timezoneIso),
  })
}
